<template>
  <div class="interactives-push-alert-animation">
    <div class="post-break">
      <img src="./assets/mobileBackground.gif" />
      <div class="interactive-credit">
        <table>
          <tr>
            <td class="by">To view the interactive, use a larger device or expand your browser window.</td>
          </tr>
        </table>
      </div>
    </div>
    <div class="pre-break">
      <div class="interactive-main">
        <div class="animation-header">
          <table>
            <tr>
              <td class="hed">The Year in Push Alerts</td>
              <td class="instructions">Below are a year’s worth of <em>New York Times</em> breaking news notifications. To navigate, move the <span class="yellow-bar">yellow bar</span> or click on an event on the timeline at the bottom. Click and drag on the columns that overflow to see any hidden alerts. Scroll down to read the rest of the package.</td>
            </tr>
          </table>
        </div>
        <div class="bg">
          <animation
            :alertsByDay="alertsByDay"
            :currentSimSec="currentSimSec"
            :userSelectedSimSec="userSelectedSimSec"
            :lag="lag"
            :filter="filter"
            :vizRendered.sync="animReady"
            :scaled.sync="daysShown"
          />
          <div class="gutter">
            <div class="gradient">
              <table class="controls">
                <tr>
                  <td class="filters">
                    <span class="filter active-button" @click="activateFilter">All push alerts</span> | 
                    <span class="filter" @click="activateFilter">Only Trump</span> | 
                    <span class="filter" @click="activateFilter">No Trump</span>
                  </td>
                  <td class="spacer"></td>
                  <td class="play active-button" @click="stopped ? playAnim() : pauseAnim()">
                    <img v-if="stopped" src="./assets/play.png"/>
                    <img v-else src="./assets/pause.png"/>
                  </td>
                </tr>
              </table>
            </div>
          </div>
          <timeline
            :margins="{
              top: 70,
              right: 60,
              bottom: 40,
              left: 60
            }"
            :moments="moments"
            :eras="eras"
            :firstSimSec="firstSimSec"
            :lastSimSec="lastSimSec"
            :currentSimSec="currentSimSec"
            :lag="lag"
            :daysShown="daysShown"
            :animReady="animReady"
            :selectedSimSec.sync="userSelectedSimSec"
            @dragStarted="dragPauseAnim"
            @dragEnded="playAnim"
          />
        </div>
        <div class="overlay">
          <div v-if="startedSmall" class="resized">Refresh the page to see the interactive.</div>
        </div>
        <div class="alert intro-alert" style="max-height: 200px">
          <div class="alert-header">
            <table>
              <tr>
                <td class="times-logo">
                  <span class="times-image"></span>
                </td>
              <td class="times-brand">NYTIMES</td>
              <td class="alert-timestamp">3:41 AM</td>
            </tr>
          </table>
          </div>
          <div class="alert-body">Donald Trump has been elected president of the United States, a stunning upset for an outsider who defied the political establishment</div>
        </div>
      </div>
      <div v-if="!startedSmall" class="interactive-credit">
        <table>
          <tr>
            <td class="photo">Photo credits: Top row, from left—Scott Olson/Getty Images, Mark Wilson/Getty Images, Andrew Caballero-Reynolds/AFP/Getty Images; second row, from left—Brendan Smialowski/AFP/Getty Images, STR/AFP/Getty Images, Stephanie Keith/Getty Images, Mark Wilson/Getty Images; third row, from left—Patrick Smith/Getty Images, Frederic J. Brown/AFP/Getty Images, Chip Somodevilla/Getty Images</td>
            <td class="by">Interactive by Andrew Kahn, with art by Holly Allen and additional help from Rachel Withers</td>
          </tr>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3'
import * as _ from 'lodash'

import Animation from './components/Animation.vue'
import Timeline from './components/Timeline.vue'

// Abbreviations:
//  Dur = duration
//  Secs = Seconds
//  Msecs = Milliseconds
//  Sim = simulated
//  Res = resolution

export default {
  name: 'PushAlertAnim',
  data () {
    return {
      alerts: null, // chronological array of alerts
      alertsByDay: null, // ditto, but nested by date
      moments: null, // timeline moments (single events)
      eras: null, // timeline eras
      currentSimSec: null, // current second shown in the simulated timeline
      resumeFrom: 0, // last moment, in real milliseconds, when the animation was paused
      lag: 0, // milliseconds between frames
      userSelectedSimSec: null, // user-selected second in simulated timeline
      realDurMins: 15, // duration of animation in real minutes
      daysShown: null, // simulated days shown on canvas
      animReady: false, // whether the animation is ready
      stopped: true, // whether the animation is paused
      filter: 'All push alerts', // state of the Trump filter
      debounce: 500, // milliseconds to debounce resizing
      controlsShown: false, // are the controls visible
      breakpoint: 800, // breakpoint for small/large displays
      startedSmall: false, // did the page start on the small setting
      windowWidth: window.innerWidth,    // used to detect whether horiz
      windowHeight: window.innerHeight  // or vert size has changed
    }
  },
  computed: {
    realDurMsecs () { return this.realDurMins * 60 * 1000 },
    firstSimSec () { if (this.alerts) return this.alerts[0].dateParsed },
    lastSimSec () { if (this.alerts) return this.alerts[this.alerts.length - 1].dateParsed },
    timeScale () { if (this.alerts) return d3.scaleTime().domain([this.firstSimSec, this.lastSimSec]).range([0, this.realDurMsecs]) }
  },
  watch: {
    animReady: function (newVal) { if (newVal) this.$emit('animReady') },
    elapsed: function (newVal, oldVal) { this.lag = newVal - oldVal },
    userSelectedSimSec: function (newVal) {
      this.currentSimSec = newVal
      this.resumeFrom = this.timeScale(this.currentSimSec)
    }
  },
  mounted () {
    d3.select('header#socialbanners_announcement.notification').style('display', 'none')
    d3.select('header#tablet_header.roll-up').style('display', 'none')
    if (window.screen.width > this.breakpoint && window.innerWidth > this.breakpoint) this.prepareLarge()
    else {
      this.prepareSmall()
      this.startedSmall = true
    }
  },
  methods: {
    prepareLarge () {
      this.$el.scrollTop = 0
      this.sizeAnimation()
      this.importData()
    },
    prepareSmall () {
      if (this.animReady) this.pauseAnim()
    },
    sizeAnimation () {
      this.int = d3.select('.interactives-push-alert-animation')
      this.int.select('.interactive-main').style('height', `${window.innerHeight}px`)
      this.sizeBackground()
    },
    importData () {
      d3.queue()
        .defer(d3.csv, '/features/vue/push/static/alerts.csv')
        .defer(d3.csv, '/features/vue/push/static/events.csv')
        .await(this.initAnim)
    },
    initAnim (error, alerts, events) {
      if (error) throw error

      const parser1 = d3.timeParse('%Y-%m-%d %H:%M:%S')
      alerts.forEach(function (d, i) {
        d.alertId = i
        d.dateParsed = parser1(d.timestamp)
        d.hasTrump = d.hasTrump === 'TRUE'
        d.highlight = d.highlight === 'TRUE'
      })
      this.alerts = alerts

      const parser2 = d3.timeParse('%-m/%d/%y')
      events.forEach(d => {
        d.startParsed = parser2(d.start)
        if (d.end !== '') d.endParsed = parser2(d.end)
      })
      this.moments = events.filter(d => d.end === '')
      this.eras = events.filter(d => d.end !== '')

      const alertsByDay = d3.timeDay.range(d3.timeDay.floor(this.firstSimSec), this.lastSimSec)
        .map(d => {
          return {
            day: d,
            alerts: alerts.filter(a => +d3.timeDay(a.dateParsed) === +d)
          }
        })
      this.alertsByDay = alertsByDay

      this.currentSimSec = this.firstSimSec
      this.$on('animReady', () => {
        this.showFirstAlert()
        window.addEventListener('resize', () => {
          this.debouncedPauseAnim()
          this.resizeLarge()
        })
        window.addEventListener('scroll', () => {
          this.checkAnimVisibility()
        })
      })
    },
    showFirstAlert () {
      const overlay = this.int.select('.overlay')
      const alert = this.int.select('.intro-alert')
      const matchAlert = this.int.select('.match-alert')
      alert.style('width', '406.219px')
      alert
        .style('top', `${(this.int.select('.interactive-main').node().clientHeight - 87.781) / 2}px`)
        .style('left', `${(this.$el.clientWidth - 406.219) / 2}px`)

      d3.timeout(() => {
        alert.classed('shown-anim', true)
      }, 800)
      d3.timeout(() => {
        const matchLoc = matchAlert.node().getBoundingClientRect()

        const t1 = alert
          .transition(2000)
          .style('left', `${matchLoc.left}px`)
          .style('top', `${matchLoc.top - this.int.node().getBoundingClientRect().top}px`)

        t1.on('end', () => {
          const t2 = overlay.transition()
            .duration(1000)
            .style('opacity', 0)
            .remove()

          t2.on('end', () => {
            alert.remove()
            d3.timeout(() => {
              this.playAnim()
              this.$emit('enableDrag')
            }, 200)
          })
        })
      }, 3800)
    },
    playAnim () {
      if (!this.controlsShown) {
        d3.timeout(this.showControls, 4000)
      }

      if (this.currentSimSec >= this.lastSimSec) {
        this.resumeFrom = 0
        this.currentSimSec = this.firstSimSec
      }
      this.startTimer()
      this.stopped = false
    },
    pauseAnim () {
      this.stopTimer()
      this.stopped = true
    },
    dragPauseAnim () {
      this.stopTimer()
    },
    startTimer (restart) {
      this.timer = d3.timer((elapsed) => {
        if (this.currentSimSec < this.lastSimSec) {
          this.elapsed = this.resumeFrom + elapsed
          this.currentSimSec = this.timeScale.invert(this.elapsed)
        } else this.pauseAnim()
      })
    },
    stopTimer () {
      this.timer.stop()
      this.resumeFrom = this.elapsed
    },
    showControls () {
      this.int.select('.controls').transition(1000).style('opacity', 1)
      this.controlsShown = true
    },
    activateFilter (e) {
      const target = e.target
      this.filter = target.innerHTML
      this.int.selectAll('.filter').classed('active-button', function () { return this === target })
    },
    sizeBackground () {
      const headerHeight = this.int.select('.animation-header').node().clientHeight
      const footerHeight = this.int.select('.interactives-push-alert-animation-timeline').node().clientHeight
      this.int.select('.bg').style('height', `${window.innerHeight - headerHeight - footerHeight}px`)
    },
    debouncedPauseAnim: _.debounce(function () {
      this.pauseAnim()
    }, 500, {'leading': true}),
    resizeLarge: _.debounce(function () {
      if (window.innerWidth < this.breakpoint) this.prepareSmall()
      else {
        if (window.innerWidth !== this.windowWidth) {
          this.$emit('resize')
          this.playAnim()
          this.$emit('enableDrag')
          this.windowWidth = window.innerWidth
        }
        if (window.innerHeight !== this.innerHeight) {
          this.sizeBackground()
          this.windowHeight = window.innerHeight
        }
      }
    }, 500),
    checkAnimVisibility: _.debounce(function () {
      const docViewTop = window.scrollY

      const elem = this.int.select('.interactives-push-alert-animation-main').node()
      const elemBottom = elem.offsetTop + elem.clientHeight

      console.log(elemBottom, docViewTop)

      // pause anim if it's not fully visible
      if (elemBottom < docViewTop) this.pauseAnim()
    }, 500)
  },
  components: {
    'animation': Animation,
    'timeline': Timeline
  }
}
</script>

<style lang="scss">
body {
  margin-top: 0;
}
body.fresca {
  padding-top: 0;
}
header#socialbanners_announcement.notification {
  display: none;
}

header#tablet_header.roll-up {
  display: none;
}

.interactives-push-alert-animation {
  width: 100%;
  box-sizing: border-box;
  font-family: 'sl-Apres', helvetica, sans-serif;

  * {
    box-sizing: border-box;
  }

  .post-break {
    display: none;
  }

  .pre-break {
    display: block;
  }

  .interactive-main {
    width: 100%;
    position: relative;
    overflow: hidden;
    box-sizing: border-box;
    font-family: 'sl-Apres', helvetica, sans-serif;

    svg text {
      font-family: 'sl-Apres', helvetica, sans-serif;
    }

    cursor: default;
    -webkit-user-select: none; /* Chrome, Opera, Safari */
    -moz-user-select: none; /* Firefox 2+ */
    -ms-user-select: none; /* IE 10+ */
    user-select: none; /* Standard syntax */

    .animation-header {
      width: 100%;
      background: rgba(47, 75, 88, 1);

      color: white;
      padding: 1em 60px;

      table {
        width: 100%;
      }

      td {
        vertical-align: center;
      }

      .hed {
        font-size: 2em;
        color: #F8E71C;
        font-family: 'sl-TitlingGothicFBCondensed', helvetica, sans-serif;
        padding-right: 0.5em;
        min-width: 380px;
      }

      .instructions {
        text-align: right;
        font-size: 0.8em;

        .yellow-bar {
          color: #F8E71C;
        }
      }
    }
    .bg {
      background-image: url("assets/newsImages.png");
      background-size: cover;
      background-repeat: no-repeat;
      background-position: center center;
      height: 80%;
      position: relative;

      .interactives-push-alert-animation-main {
        position: relative;
        height: 100%;

        .ticker {
          position: relative;
          height: 60px;
          background: -webkit-linear-gradient(bottom, rgba(47, 75, 88, 1), rgba(47, 75, 88, 0)); /* For Safari 5.1 to 6.0 */
          background: -o-linear-gradient(bottom, rgba(47, 75, 88, 1), rgba(47, 75, 88, 0)); /* For Opera 11.1 to 12.0 */
          background: -moz-linear-gradient(bottom, rgba(47, 75, 88, 1), rgba(47, 75, 88, 0)); /* For Firefox 3.6 to 15 */
          background: linear-gradient(to bottom, rgba(47, 75, 88, 1), rgba(47, 75, 88, 0)); /* Standard syntax */
        }
      }
    }
    .gutter {
      position: absolute;
      width: 100%;
      bottom: 0px;

      .gradient {
        position: relative;
        height: 60px;
        background: -webkit-linear-gradient(top, rgba(47, 75, 88, 1), rgba(47, 75, 88, 0)); /* For Safari 5.1 to 6.0 */
        background: -o-linear-gradient(top, rgba(47, 75, 88, 1), rgba(47, 75, 88, 0)); /* For Opera 11.1 to 12.0 */
        background: -moz-linear-gradient(top, rgba(47, 75, 88, 1), rgba(47, 75, 88, 0)); /* For Firefox 3.6 to 15 */
        background: linear-gradient(to top, rgba(47, 75, 88, 1), rgba(47, 75, 88, 0)); /* Standard syntax */
        
        display: -webkit-box;
        display: -ms-flexbox;
        display: flex;
        -webkit-box-align: center;
        -ms-flex-align: center;
        align-items: center;
        -webkit-box-pack: center;
        -ms-flex-pack: center;
        justify-content: center;

        .controls {
          position: absolute;
          bottom: 50%;
          color: white;
          font-size: 0.8em;
          opacity: 0;

          .filters, .play {
            padding: 0.9em;
            background: rgba(45, 77, 88, 0.7);
          }

          .spacer {
            width: 3em;
          }

          .play img {
            height: 2em;
          }

          .filter, .play {
            position: relative;
            
            cursor: pointer;

            &:active {
              top: 1px;
            }
          }

          .filter {
            text-transform: uppercase;
            font-size: 0.8em;

            &:not(:first-child) {
              margin-left: 0.25em;
            }

            &:not(:last-child) {
              margin-right: 0.25em;
            }
          }

          .active-button {
            color: #F8E71C;
            font-weight: bold;
            cursor: pointer;
          }
        }
      }
    }

    .interactives-push-alert-animation-timeline {
      height: 140px;
      background: rgba(47, 75, 88, 1);
    }

    .overlay {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: black;

      display: -webkit-box;
      display: -ms-flexbox;
      display: flex;
      -webkit-box-align: center;
      -ms-flex-align: center;
      align-items: center;
      -webkit-box-pack: center;
      -ms-flex-pack: center;
      justify-content: center;

      .resized {
        max-width: 444px;
        color: white;
        font-size: 2em;
      }
    }

    .date {
      width: 444px;
      display: flex;
      align-items: center;
      justify-content: center;
      height: 3em;
      position: absolute;
      transform: rotate(0.01deg); /* fixes bug on FF */
      color: white;
      font-size: 1.5em;
      text-align: center;
    }

    .column {
      width: 444px;
      position: absolute;
      padding: 0.7em;
      padding-top: 0;
      transform: rotate(0.01deg); /* fixes bug on FF */

      .alerts {
        padding-top: 0.8em;
      }
    }

    .alert {
      opacity: 0;
      font-size: 0.8em;
      font-family: 'HelveticaNeue', 'Helvetica';

      &.intro-alert {
        position: absolute;
      }

      &.shown-anim {
        animation-name: showAlert;
        animation-duration: 0.25s;
        opacity: 1;
        display: block;
      }

      &.alert-highlight:after {
        content: "";
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        box-shadow: 0 0 2em #F8E71C;
        border-radius: 1em;
      }

      .alert-header {
        position: relative;
        background: #CCCCCC;
        border-top-right-radius: 1em;
        border-top-left-radius: 1em;
        vertical-align: middle;
        width: 100%;
        text-align: left;
        padding: 0.2em 0;

        table {
          width: 100%;
        }

        tr {
          width: 100%;
          height: 25px;
        }

        .times-image {
          width: 25px;
          height: 25px;
          vertical-align: middle;
          background-image: url('assets/times.png');
          background-size: 25px 25px;
          display: inline-block;
        }

        .times-logo {
          height: 25px;
          padding-left: 1em;
        }

        .times-brand {
          width: 50%;
          padding: 0 1em;
          padding-left: 0.5em;
        }

        .alert-timestamp {
          width: 50%;
          padding: 0 1em;
          text-align: right;
          opacity: 0.4;
        }
      }

      .alert-body {
        background: #c7c7c7;
        padding: 1em;
        background: #F0F0F1;
        border-bottom-right-radius: 1em;
        border-bottom-left-radius: 1em;
        text-align: left;
      }
    }

    .draggable {
      cursor: grab;
      &.dragging {
        cursor: grabbing;
      }
    }
  }

  .interactive-credit {
    width: 100%;
    padding: 0.4em;

    table {
      width: 100%;
    }

    .photo {
      max-width: 500px;
      font-size: 0.6em;
    }

    .by {
      text-align: right;
      vertical-align: top;
      font-size: 0.8em;
    }
  }
}

@keyframes showAlert {
	0% { opacity: 0; transform: scale3d(0.1,0,1); }
	100% { opacity: 1; transform: scale3d(1,1,1); }
}

@media screen and (max-width:800px) {
  .interactives-push-alert-animation {
    .pre-break {
      display: none;
    }
    .post-break {
      display: block;
      width: 100%;
      
      img {
        width: 100%;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// App.vue?51f5a817